import { useCallback, useEffect, useRef, useState } from 'react'
import { useMachine } from '@xstate/react'
import { api, AuthError } from '../api'
import { audioStore } from './audioStore'
import { loadHistoryCache, saveHistoryCache } from '../historyCache'
import { StreamingTTS } from '../tts'
import {
    chatMachine,
    selectCapturing,
    selectMode,
    selectStatus,
    selectStatusText,
    type Message,
    type Mode,
    type Status
} from './chatMachine'

export type { Message } from './chatMachine'

export interface ChatSession {
    messages: Message[]
    liveTranscript: string
    mode: Mode
    status: Status
    statusText: string
    isReplying: boolean
    muted: boolean
    capturing: boolean
    error: string | null
    input: string
    setInput: (v: string) => void
    sendText: () => void
    interrupt: () => void
    toggleMute: () => void
    enterVoice: () => void
    enterText: () => void
    manualStart: () => void
    manualStop: () => void
    manualCancel: () => void
    hasMore: boolean
    loadingMore: boolean
    loadMore: () => void
}

export function useChatSession({ persona, onLogout }: { persona: string; onLogout: () => void }): ChatSession {
    const [input, setInput] = useState('')
    const [state, send] = useMachine(chatMachine, { input: { persona, onLogout } })
    // 上滑翻页:cursor = 下一次更早一页的 beforeId(服务端 nextCursor),null 即到底
    const [hasMore, setHasMore] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const cursorRef = useRef<number | null>(null)
    const loadingRef = useRef(false) // 同步去重,挡住滚动连发

    const mode = selectMode(state)
    const status = selectStatus(state)
    const statusText = selectStatusText(state)
    const isReplying = status === 'thinking' || status === 'speaking'
    const capturing = selectCapturing(state)
    const { messages, liveTranscript, muted, error } = state.context

    // 进 Chat 时载入本地已有 AI 音频的 key(重载/重登后历史气泡据此显示播放按钮)
    useEffect(() => {
        void audioStore.init()
    }, [])

    // persona 变更同步进机器(turnRunner 据此请求)
    useEffect(() => {
        send({ type: 'SET_PERSONA', persona })
    }, [persona, send])

    // TTS 是浏览器资源(AudioContext),随 persona 重建,作为资源句柄注入机器
    useEffect(() => {
        const tts = new StreamingTTS({ persona }).on('error', (err) => console.warn('[chat] tts error:', err))
        send({ type: 'SET_TTS', tts })
        return () => {
            send({ type: 'SET_TTS', tts: null })
            tts.dispose()
        }
    }, [persona, send])

    // 历史:stale-while-revalidate —— 先读本地缓存秒显,再拉服务器最新替换(服务器为真)
    const didInit = useRef(false)
    useEffect(() => {
        if (didInit.current) return
        didInit.current = true
        const cached = loadHistoryCache()
        if (cached && cached.length) send({ type: 'INIT_MESSAGES', messages: cached })

        api.history
            .list('chat', { pageSize: 20 })
            .then((res) => {
                const rows = (res.list || []).slice().reverse()
                if (rows.length === 0) {
                    const seed: Message[] = [{ id: Date.now(), role: 'assistant', content: 'Hi there!' }]
                    send({ type: 'INIT_MESSAGES', messages: seed })
                    saveHistoryCache(seed)
                    return
                }
                const msgs: Message[] = []
                for (const row of rows) {
                    msgs.push({ id: row._id * 2, role: 'user', content: row.userText })
                    if (row.aiText) msgs.push({ id: row._id * 2 + 1, role: 'assistant', content: row.aiText })
                }
                send({ type: 'INIT_MESSAGES', messages: msgs })
                saveHistoryCache(msgs)
                cursorRef.current = res.nextCursor // 满页才非空 → 还有更早的
                setHasMore(res.nextCursor != null)
            })
            .catch((err) => {
                if (err instanceof AuthError) {
                    onLogout()
                    return
                }
                console.warn('[chat] history load failed:', err)
                if (!cached || !cached.length) {
                    send({ type: 'INIT_MESSAGES', messages: [{ id: Date.now(), role: 'assistant', content: 'Hi there!' }] })
                }
            })
    }, [onLogout, send])

    const sendText = useCallback(() => {
        if (isReplying) return
        const text = input.trim()
        if (!text) return
        setInput('')
        send({ type: 'SEND_TEXT', text })
    }, [isReplying, input, send])

    const interrupt = useCallback(() => send({ type: 'INTERRUPT' }), [send])
    const toggleMute = useCallback(() => send({ type: muted ? 'UNMUTE' : 'MUTE' }), [muted, send])
    const enterVoice = useCallback(() => send({ type: 'ENTER_VOICE' }), [send])
    const enterText = useCallback(() => send({ type: 'ENTER_TEXT' }), [send])
    const manualStart = useCallback(() => send({ type: 'MANUAL_START' }), [send])
    const manualStop = useCallback(() => send({ type: 'MANUAL_STOP' }), [send])
    const manualCancel = useCallback(() => send({ type: 'MANUAL_CANCEL' }), [send])

    // 上滑到顶 → 拉更早一页历史,前插(滚动位置保持由 MessageList 负责)
    const loadMore = useCallback(async () => {
        if (loadingRef.current || cursorRef.current == null) return
        loadingRef.current = true
        setLoadingMore(true)
        try {
            const res = await api.history.list('chat', { pageSize: 20, beforeId: cursorRef.current })
            const rows = (res.list || []).slice().reverse()
            const older: Message[] = []
            for (const row of rows) {
                older.push({ id: row._id * 2, role: 'user', content: row.userText })
                if (row.aiText) older.push({ id: row._id * 2 + 1, role: 'assistant', content: row.aiText })
            }
            cursorRef.current = res.nextCursor
            setHasMore(res.nextCursor != null)
            if (older.length) send({ type: 'PREPEND_MESSAGES', messages: older })
        } catch (err) {
            if (err instanceof AuthError) {
                onLogout()
                return
            }
            console.warn('[chat] load more history failed:', err)
        } finally {
            loadingRef.current = false
            setLoadingMore(false)
        }
    }, [send, onLogout])

    return {
        messages,
        liveTranscript,
        mode,
        status,
        statusText,
        isReplying,
        muted,
        capturing,
        error,
        input,
        setInput,
        sendText,
        interrupt,
        toggleMute,
        enterVoice,
        enterText,
        manualStart,
        manualStop,
        manualCancel,
        hasMore,
        loadingMore,
        loadMore,
    }
}
